import { useEffect, useState } from 'react';
import { Table, Typography } from 'antd';
import './index.scss';
import CustomTable from '../table';
import { Franchise } from '@/types';
import { Loader } from '@/components/layout/DefaultLayout';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

const { Text } = Typography;

// Table columns

interface Props {
  franchisors: Franchise[];
  columns: any[];
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  limit: number;
  setLimit: React.Dispatch<React.SetStateAction<number>>;
  total: number;
  downloadData?: (record: any) => void;
  isExpandable?: boolean;
  isMappingPage?: boolean;
}

export default function Franchisers({
  franchisors,
  columns,
  page,
  setPage,
  limit,
  setLimit,
  total,
  downloadData,
  isExpandable = true,
  isMappingPage = false,
}: Props) {
  const [filteredData, setFilteredData] = useState<Franchise[] | undefined>([]);
  const domain = window.location.origin;
  useEffect(() => {
    const formattedData = franchisors?.map((franchisor) => ({
      ...franchisor,
      key: franchisor?._id,
      contactPerson: `${franchisor.firstName} ${franchisor.lastName}`,
    }));

    setFilteredData(formattedData);
  }, [franchisors]);

  const franchableLastUpdated = franchisors
    ?.map((franchisor) => franchisor.franchableNameUpdatedAt)
    .filter((date) => date !== undefined)
    .sort((a, b) => new Date(b!).getTime() - new Date(a!).getTime())[0];
  
  const franchableLastUpdatedBefore = // difference in minutes between now and franchableLastUpdated
    franchableLastUpdated && new Date(franchableLastUpdated).getTime() < Date.now()
      ? Math.floor((Date.now() - new Date(franchableLastUpdated).getTime()) / 60000)
      : 'N/A';
  
  const lastUpdated = franchisors
    ?.map((franchisor) => franchisor.updatedAt)
    .filter((date) => date !== undefined)
    .sort((a, b) => new Date(b!).getTime() - new Date(a!).getTime())[0];
  
  const lastUpdatedBefore = lastUpdated && new Date(lastUpdated).getTime() < Date.now()
      ? Math.floor((Date.now() - new Date(lastUpdated).getTime()) / 60000)
      : 'N/A';
  
  const UpdatedBefore = isMappingPage ? franchableLastUpdatedBefore : lastUpdatedBefore;
  

  if (!filteredData) return <Loader />;

  return (
    <>
      <div className="tables-info">
        <Text style={{ color: '#9FA1AD', fontWeight: 500 }}>
          {total} Total Franchises
        </Text>
        <span className="tables-divider" />
        <Text style={{ color: '#9FA1AD', fontWeight: 500 }}>
          Updated {UpdatedBefore} min ago
        </Text>
      </div>
      <CustomTable
        data={franchisors as any}
        columns={columns}
        downloadData={downloadData}
        filteredData={filteredData}
        setFilteredData={setFilteredData}
        total={total}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        label="Franchises"
        {...(isExpandable && {
          expandable: {
            expandedRowRender: (record) => {
              return (
                <Table
                  columns={[
                    {
                      title: 'User',
                      dataIndex: 'fullName',
                      key: 'fullName',
                      render: (_: any, franchisor: any) =>
                        `${franchisor.firstName} ${franchisor.lastName}`,
                    },
                    {
                      title: 'Phone',
                      dataIndex: 'phone',
                      key: 'phone',
                    },
                    {
                      title: 'SMS Consent',
                      dataIndex: 'smsConsent',
                      key: 'smsConsent',
                      render: (value: boolean | undefined) => (
                        <Typography.Text>
                          {value === undefined ? 'No' : value ? 'Yes' : 'No'}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: 'Email',
                      dataIndex: 'email',
                      key: 'email',
                    },
                    {
                      title: 'Status',
                      dataIndex: 'isVerified',
                      key: 'isVerified',
                      render: (status: string, franchisor: Franchise) => {
                        let statusColor = '#1C8271';
                        let statusText = 'Activated';
                        if (status) {
                          statusColor = '#1C8271';
                          statusText = 'Activated';
                        }
                        if (!franchisor.status) {
                          statusColor = '#F6C38B';
                          statusText = 'Hidden';
                        }
                        if (franchisor.isDeleted) {
                          statusColor = '#F46262';
                          statusText = 'Deleted';
                        }
                        if (franchisor.isDeactivated) {
                          statusColor = '#2A4BC2';
                          statusText = 'Deactivated';
                        }
                        if (!status) {
                          statusColor = '#F68B8B';
                          statusText = 'Inactive';
                        }
                        return (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                              }}
                            />
                            <Typography.Text>{statusText}</Typography.Text>
                          </div>
                        );
                      },
                    },
                    // {
                    //   title: 'Activation Link',
                    //   dataIndex: 'tokenActivation',
                    //   key: 'tokenActivation',
                    //   render: (tokenActivation: string) =>
                    //     tokenActivation ? (
                    //       <Link
                    //         to={`${domain}/activate-profile-franchisor?token=${tokenActivation}`}
                    //       >
                    //         Activate
                    //       </Link>
                    //     ) : (
                    //       `N/A`
                    //     ),
                    // },
                    // {
                    //   title: 'Token Expiration',
                    //   dataIndex: 'tokenActivationExpiresAt',
                    //   key: 'tokenActivationExpiresAt',
                    //   render: (tokenActivationExpiresAt: string) =>
                    //     tokenActivationExpiresAt ? (
                    //       <div
                    //         style={{
                    //           display: 'flex',
                    //           alignItems: 'center',
                    //           gap: 8,
                    //         }}
                    //       >
                    //         <Typography.Text>
                    //           {format(
                    //             new Date(tokenActivationExpiresAt),
                    //             'd MMM, yyyy, h:mm a',
                    //           )}
                    //         </Typography.Text>
                    //       </div>
                    //     ) : (
                    //       `N/A`
                    //     ),
                    // },
                  ]}
                  dataSource={record.franchisors || []}
                  pagination={false}
                  rowKey={(franchisor) => franchisor._id}
                />
              );
            },
            rowExpandable: (record) => (record.franchisors || []).length > 0,
          },
        })}
        isDisabled={!(franchisors.length > 0)}
      />
    </>
  );
}
